import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Course, Entitlement, CourseLesson, LessonProgress, Exam } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { BookOpen, CheckCircle, Calendar, Award } from 'lucide-react'

interface LessonWithProgress extends CourseLesson {
  progress?: LessonProgress
}

export function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null)
  const [lessons, setLessons] = useState<LessonWithProgress[]>([])
  const [exam, setExam] = useState<Exam | null>(null)

  // 講座タイトルが取得できたら動的に設定
  useDocumentTitle(course?.title || '講座詳細')

  useEffect(() => {
    if (!user || !courseId) return
    fetchCourseData()
  }, [courseId, user])

  const fetchCourseData = async () => {
    if (!courseId || !user) return

    try {
      setLoading(true)
      setError(null)

      // 講座情報を取得
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_id', courseId)
        .single()

      if (courseError || !courseData) {
        setError('講座が見つかりませんでした')
        setLoading(false)
        return
      }

      setCourse(courseData)

      // 受講権を確認
      const { data: entitlementData, error: entitlementError } = await supabase
        .from('entitlements')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('status', 'active')
        .single()

      if (entitlementError || !entitlementData) {
        setError('この講座の受講権がありません')
        setLoading(false)
        return
      }

      // 受講期間チェック
      const now = new Date()
      if (entitlementData.valid_from && new Date(entitlementData.valid_from) > now) {
        setError('受講開始日前です')
        setLoading(false)
        return
      }

      if (entitlementData.valid_until && new Date(entitlementData.valid_until) < now) {
        setError('受講期間が終了しています')
        setLoading(false)
        return
      }

      setEntitlement(entitlementData)

      // レッスン一覧を取得
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('course_id', courseId)
        .order('sort_order', { ascending: true })

      if (lessonsError) throw lessonsError

      // 各レッスンの進捗を取得
      const { data: progressData } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)

      const lessonsWithProgress: LessonWithProgress[] = (lessonsData || []).map((lesson) => {
        const progress = progressData?.find((p) => p.lesson_id === lesson.lesson_id)
        return {
          ...lesson,
          progress,
        }
      })

      setLessons(lessonsWithProgress)

      // 試験情報を取得
      const { data: examData } = await supabase
        .from('exams')
        .select('*')
        .eq('course_id', courseId)
        .single()

      if (examData) {
        setExam(examData)
      }
    } catch (err: any) {
      console.error('Error fetching course data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const completedLessons = lessons.filter((l) => l.progress?.status === 'completed').length
  const totalLessons = lessons.length
  const allLessonsCompleted = totalLessons > 0 && completedLessons === totalLessons

  if (loading) {
    return (
      <MainLayout>
        <Loading message="講座情報を読み込み中..." />
      </MainLayout>
    )
  }

  if (error || !course || !entitlement) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '講座情報が見つかりませんでした'} />
          <div className="mt-4">
            <button
              onClick={() => navigate('/mypage')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </MainLayout>
    )
  }

  const progressPercentage =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f9fafb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
          {/* 講座ヘッダー */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{course.title}</h1>
            <p className="text-gray-600 mb-4 leading-relaxed">
              {course.description || '講座の説明はありません'}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {course.ambassador_name && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                  <span className="font-medium">講師: {course.ambassador_name}</span>
                </div>
              )}

              {entitlement.valid_until && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                  <Calendar className="h-4 w-4" />
                  <span>
                    有効期限: {new Date(entitlement.valid_until).toLocaleDateString('ja-JP')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 進捗サマリー */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">学習進捗</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {completedLessons} / {totalLessons} レッスン完了
                  </span>
                  <span className="text-sm font-bold text-gray-900">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-[#006cc6] h-2.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${progressPercentage}%`,
                    }}
                  />
                </div>
              </div>

              {allLessonsCompleted && exam && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-green-800 font-semibold mb-1">
                        すべてのレッスンが完了しました
                      </p>
                      <p className="text-sm text-green-700 mb-3">
                        試験に挑戦して認定証を取得しましょう
                      </p>
                      <button
                        onClick={() => navigate(`/exams/${exam.exam_id}/start`)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium shadow-sm"
                      >
                        <Award className="h-4 w-4" />
                        試験を受験する
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* レッスン一覧 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">レッスン</h2>
            </div>

            {lessons.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-sm">まだレッスンがありません</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {lessons.map((lesson, index) => {
                  const isCompleted = lesson.progress?.status === 'completed'
                  return (
                    <li key={lesson.lesson_id}>
                      <button
                        onClick={() =>
                          navigate(`/courses/${courseId}/lessons/${lesson.lesson_id}`)
                        }
                        className="w-full px-6 py-4 hover:bg-gray-50 transition-colors text-left flex items-center gap-4 group"
                      >
                        <div className="flex-shrink-0">
                          {isCompleted ? (
                            <div className="h-8 w-8 rounded-full bg-green-50 flex items-center justify-center">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 flex items-center justify-center group-hover:border-[#006cc6] transition-colors">
                              <span className="text-xs font-medium text-gray-500 group-hover:text-[#006cc6]">
                                {index + 1}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-medium text-gray-900 mb-0.5 group-hover:text-[#006cc6] transition-colors">
                            {lesson.title}
                          </h3>
                          {lesson.description && (
                            <p className="text-sm text-gray-600 line-clamp-1">
                              {lesson.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                          {lesson.lesson_type === 'text' && (
                            <span className="px-2 py-1 bg-gray-100 rounded">テキスト</span>
                          )}
                          {lesson.lesson_type === 'pdf' && (
                            <span className="px-2 py-1 bg-gray-100 rounded">PDF</span>
                          )}
                          {lesson.lesson_type === 'video_link' && (
                            <span className="px-2 py-1 bg-gray-100 rounded">動画</span>
                          )}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
